import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import AdminLayout from '@/components/AdminLayout';
import { Card } from '@/components/ui/card';
import { Calendar } from '@/components/ui/calendar';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { 
  CalendarIcon, DollarSign, Banknote, CreditCard, 
  ArrowLeftRight, Receipt, Scissors, Package, Store, Clock, User, ShoppingBag,
  Trash2, RotateCcw, Ban, Wrench, AlertCircle
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDailyCut() {
  const { user, getAuthHeaders } = useAuth();
  const [date, setDate] = useState(new Date());
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState('all');
  const [cutData, setCutData] = useState(null);
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [selectedSale, setSelectedSale] = useState(null);
  const [diagnostic, setDiagnostic] = useState(null);
  const [recomputingSale, setRecomputingSale] = useState(null);
  const [assignDialog, setAssignDialog] = useState({ open: false, sale: null, items: [], barberById: {} });
  const [allBarbers, setAllBarbers] = useState([]);
  const [actionLoading, setActionLoading] = useState(false);

  const isMasterAdmin = user?.role === 'master_admin';

  useEffect(() => {
    fetchBranches();
  }, []);

  useEffect(() => {
    fetchDailyCut();
    fetchSales();
    fetchDiagnostic();
    fetchBarbers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, selectedBranch]);

  const fetchBarbers = async () => {
    try {
      const r = await axios.get(`${API}/barbers`, getAuthHeaders());
      setAllBarbers(r.data || []);
    } catch (_e) { /* silent */ }
  };

  const barberNameOf = (item) => {
    if (!item) return null;
    // Multi-barber distribution
    if (item.barber_distribution?.length > 0) {
      const names = item.barber_distribution
        .map((d) => allBarbers.find((b) => b.id === d.barber_id)?.name || '?')
        .join(' + ');
      return names || null;
    }
    if (!item.barber_id) return null;
    const b = allBarbers.find((x) => x.id === item.barber_id);
    return b?.name || null;
  };

  const fetchBranches = async () => {
    try {
      const response = await axios.get(`${API}/branches`, getAuthHeaders());
      let availableBranches = response.data;
      
      // Manager and Admin can only see their assigned branch
      if ((user?.role === 'manager' || user?.role === 'admin') && user?.branch_id) {
        availableBranches = response.data.filter(b => b.id === user.branch_id);
        setSelectedBranch(user.branch_id);
      }
      
      setBranches(availableBranches);
    } catch (error) {
      // error handled by toast
    }
  };

  const fetchDailyCut = async () => {
    setLoading(true);
    try {
      const dateStr = format(date, 'yyyy-MM-dd');
      const branchParam = selectedBranch !== 'all' ? `&branch_id=${selectedBranch}` : '';
      const response = await axios.get(
        `${API}/reports/daily-cut?date=${dateStr}${branchParam}`,
        getAuthHeaders()
      );
      setCutData(response.data);
    } catch (error) {
      toast.error('Error al cargar corte del día');
    } finally {
      setLoading(false);
    }
  };

  const fetchSales = async () => {
    try {
      const dateStr = format(date, 'yyyy-MM-dd');
      const branchParam = selectedBranch !== 'all' ? `&branch_id=${selectedBranch}` : '';
      const response = await axios.get(
        `${API}/sales?date_from=${dateStr}T00:00:00&date_to=${dateStr}T23:59:59${branchParam}`,
        getAuthHeaders()
      );
      setSales(response.data);
    } catch (error) {
      // error handled by toast
    }
  };

  const fetchDiagnostic = async () => {
    if (user?.role !== 'master_admin') return;
    try {
      const dateStr = format(date, 'yyyy-MM-dd');
      const branchParam = selectedBranch !== 'all' ? `&branch_id=${selectedBranch}` : '';
      const res = await axios.get(
        `${API}/sales/diagnose-commissions?date=${dateStr}${branchParam}`,
        getAuthHeaders()
      );
      setDiagnostic(res.data);
    } catch (_e) { /* silent */ }
  };

  const salesWithIssues = () => {
    if (!diagnostic) return new Set();
    const s = new Set();
    (diagnostic.sales || []).forEach((sale) => {
      const missing = (sale.items || []).some((it) => !it.has_commission_record);
      if (missing) s.add(sale.sale_id);
    });
    return s;
  };

  const recomputeSaleCommissions = async (sale) => {
    // Check which items need help — those without barber_id
    const problemItems = (diagnostic?.sales || [])
      .find((s) => s.sale_id === sale.id)?.items
      ?.filter((it) => !it.has_commission_record) || [];
    const needsBarber = problemItems.some((it) => !it.recorded_barber_id || it.reason_if_missing === 'no_barber_id' || it.reason_if_missing === 'receptionist_on_service' || it.reason_if_missing === 'unknown_barber_id');

    if (needsBarber) {
      // Open assignment dialog
      if (allBarbers.length === 0) {
        try {
          const r = await axios.get(`${API}/barbers`, getAuthHeaders());
          setAllBarbers(r.data || []);
        } catch (_e) { /* silent */ }
      }
      const initial = {};
      problemItems.forEach((it) => {
        if (it.recorded_barber_id && it.reason_if_missing !== 'no_barber_id') {
          initial[it.item_index] = it.recorded_barber_id; // pre-fill with recorded (in case it's valid barber and just missing write)
        }
      });
      setAssignDialog({ open: true, sale, items: problemItems, barberById: initial });
      return;
    }

    // No override needed → just call recompute
    await callRecompute(sale.id, []);
  };

  const callRecompute = async (saleId, overrides) => {
    setRecomputingSale(saleId);
    try {
      const res = await axios.post(
        `${API}/sales/${saleId}/recompute-commissions`,
        { overrides },
        getAuthHeaders()
      );
      const created = res.data?.created || 0;
      if (created > 0) {
        toast.success(`${created} comisión${created !== 1 ? 'es' : ''} regenerada${created !== 1 ? 's' : ''}`);
      } else {
        toast.info('Sin cambios');
      }
      await fetchDiagnostic();
      setAssignDialog({ open: false, sale: null, items: [], barberById: {} });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al recomputar');
    } finally {
      setRecomputingSale(null);
    }
  };

  const confirmAssignment = () => {
    const overrides = Object.entries(assignDialog.barberById)
      .filter(([_, bid]) => !!bid)
      .map(([idx, bid]) => ({ item_index: parseInt(idx), barber_id: bid }));
    if (overrides.length === 0) {
      toast.error('Asigna al menos un barbero');
      return;
    }
    callRecompute(assignDialog.sale.id, overrides);
  };

  const handleCancelSale = async () => {
    if (!selectedSale) return;
    
    setActionLoading(true);
    try {
      await axios.post(
        `${API}/sales/${selectedSale.id}/cancel`,
        {},
        getAuthHeaders()
      );
      toast.success('Venta cancelada exitosamente');
      fetchDailyCut();
      fetchSales();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cancelar venta');
    } finally {
      setActionLoading(false);
      setCancelDialogOpen(false);
      setSelectedSale(null);
    }
  };

  const handleRestoreSale = async (sale) => {
    setActionLoading(true);
    try {
      await axios.post(
        `${API}/sales/${sale.id}/restore`,
        {},
        getAuthHeaders()
      );
      toast.success('Venta restaurada exitosamente');
      fetchDailyCut();
      fetchSales();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al restaurar venta');
    } finally {
      setActionLoading(false);
    }
  };

  const openCancelDialog = (sale) => {
    setSelectedSale(sale);
    setCancelDialogOpen(true);
  };

  const handleCancelItem = async (sale, itemIndex) => {
    setActionLoading(true);
    try {
      await axios.post(
        `${API}/sales/${sale.id}/items/${itemIndex}/cancel`,
        {},
        getAuthHeaders()
      );
      toast.success('Item cancelado exitosamente');
      fetchDailyCut();
      fetchSales();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cancelar item');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRestoreItem = async (sale, itemIndex) => {
    setActionLoading(true);
    try {
      await axios.post(
        `${API}/sales/${sale.id}/items/${itemIndex}/restore`,
        {},
        getAuthHeaders()
      );
      toast.success('Item restaurado exitosamente');
      fetchDailyCut();
      fetchSales();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al restaurar item');
    } finally {
      setActionLoading(false);
    }
  };

  const getPaymentMethodIcon = (method) => {
    switch (method) {
      case 'cash': return <Banknote className="w-4 h-4 text-green-500" />;
      case 'card': return <CreditCard className="w-4 h-4 text-blue-500" />;
      case 'transfer': return <ArrowLeftRight className="w-4 h-4 text-purple-500" />;
      default: return <DollarSign className="w-4 h-4 text-zinc-500" />;
    }
  };

  const getPaymentMethodLabel = (method) => {
    switch (method) {
      case 'cash': return 'Efectivo';
      case 'card': return 'Tarjeta';
      case 'transfer': return 'Transferencia';
      default: return method;
    }
  };

  const StatCard = ({ title, value, icon: Icon, subtitle, large = false }) => (
    <Card className={`bg-zinc-900/50 border-zinc-800 p-5 ${large ? 'col-span-2' : ''}`}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs text-zinc-500 uppercase tracking-widest mb-1">{title}</p>
          <p className={`font-heading ${large ? 'text-4xl' : 'text-2xl'} text-gold`}>
            {value}
          </p>
          {subtitle && <p className="text-sm text-zinc-500 mt-1">{subtitle}</p>}
        </div>
        <div className="w-10 h-10 rounded-sm bg-gold/10 flex items-center justify-center">
          <Icon className="w-5 h-5 text-gold" />
        </div>
      </div>
    </Card>
  );

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-daily-cut">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="font-heading text-3xl text-white tracking-tight">Corte del Día</h1>
            <p className="text-zinc-500 mt-1">Resumen de caja por día</p>
          </div>
          <div className="flex gap-3">
            <Select 
              value={selectedBranch} 
              onValueChange={setSelectedBranch}
              disabled={!isMasterAdmin && user?.branch_id}
            >
              <SelectTrigger className="w-[180px] bg-zinc-900 border-zinc-800 text-white">
                <SelectValue placeholder="Sucursal" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800">
                {isMasterAdmin && (
                  <SelectItem value="all" className="text-white">Todas las sucursales</SelectItem>
                )}
                {branches.map((branch) => (
                  <SelectItem key={branch.id} value={branch.id} className="text-white">
                    {branch.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="w-[180px] justify-start text-left font-normal bg-zinc-900 border-zinc-800 text-white hover:bg-zinc-800"
                  data-testid="date-picker-btn"
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {format(date, 'dd MMM yyyy', { locale: es })}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 bg-zinc-900 border-zinc-800" align="end">
                <Calendar
                  mode="single"
                  selected={date}
                  onSelect={(d) => d && setDate(d)}
                  initialFocus
                  className="bg-zinc-900"
                />
              </PopoverContent>
            </Popover>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12 text-zinc-500">Cargando...</div>
        ) : (
          <>
            {/* Main Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard 
                title="Total del Día" 
                value={`$${cutData?.total_sales?.toFixed(2) || '0.00'}`}
                icon={DollarSign}
                subtitle={`${cutData?.transactions_count || 0} transacciones`}
                large
              />
              <StatCard 
                title="Efectivo" 
                value={`$${cutData?.total_cash?.toFixed(2) || '0.00'}`}
                icon={Banknote}
              />
              <StatCard 
                title="Tarjeta" 
                value={`$${cutData?.total_card?.toFixed(2) || '0.00'}`}
                icon={CreditCard}
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <StatCard 
                title="Transferencia" 
                value={`$${cutData?.total_transfer?.toFixed(2) || '0.00'}`}
                icon={ArrowLeftRight}
              />
              <StatCard 
                title="Servicios" 
                value={cutData?.services_sold || 0}
                icon={Scissors}
              />
              <StatCard 
                title="Productos" 
                value={cutData?.products_sold || 0}
                icon={Package}
              />
            </div>

            {/* By Branch */}
            {cutData?.by_branch?.length > 0 && (
              <Card className="bg-zinc-900/50 border-zinc-800 p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Store className="w-4 h-4 text-gold" />
                  <h3 className="text-sm text-zinc-500 uppercase tracking-widest">
                    Desglose por Sucursal
                  </h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-zinc-800">
                        <th className="text-left text-xs text-zinc-500 uppercase tracking-widest pb-3">Sucursal</th>
                        <th className="text-right text-xs text-zinc-500 uppercase tracking-widest pb-3">Ventas</th>
                        <th className="text-right text-xs text-zinc-500 uppercase tracking-widest pb-3">Efectivo</th>
                        <th className="text-right text-xs text-zinc-500 uppercase tracking-widest pb-3">Tarjeta</th>
                        <th className="text-right text-xs text-zinc-500 uppercase tracking-widest pb-3">Transfer</th>
                        <th className="text-right text-xs text-zinc-500 uppercase tracking-widest pb-3">Trans.</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cutData.by_branch.map((branch) => (
                        <tr key={branch.branch_id} className="border-b border-zinc-800/50 last:border-0">
                          <td className="py-3 text-white">{branch.branch_name}</td>
                          <td className="py-3 text-right text-gold font-semibold">${branch.total_sales.toFixed(2)}</td>
                          <td className="py-3 text-right text-zinc-400">${branch.total_cash.toFixed(2)}</td>
                          <td className="py-3 text-right text-zinc-400">${branch.total_card.toFixed(2)}</td>
                          <td className="py-3 text-right text-zinc-400">${branch.total_transfer.toFixed(2)}</td>
                          <td className="py-3 text-right text-zinc-400">{branch.transactions_count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}

            {/* Empty State */}
            {(!cutData?.by_branch || cutData.by_branch.length === 0) && cutData?.total_sales === 0 && sales.filter(s => !s.cancelled).length === 0 && (
              <Card className="bg-zinc-900/50 border-zinc-800 p-12 text-center">
                <Receipt className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
                <p className="text-zinc-500">No hay ventas registradas para esta fecha</p>
              </Card>
            )}

            {/* Diagnostic Alert (master admin only) */}
            {isMasterAdmin && diagnostic && diagnostic.items_missing_or_pending > 0 && (
              <Card className="bg-red-500/5 border-red-500/30 p-4" data-testid="diagnostic-alert">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-red-300 font-medium text-sm">
                      {diagnostic.items_missing_or_pending} item{diagnostic.items_missing_or_pending !== 1 ? 's' : ''} sin comisión registrada
                    </p>
                    <p className="text-xs text-zinc-400 mt-0.5">
                      Estos items aparecen en ventas pero no generaron comisión para ningún barbero. Usa el botón &quot;Recomputar&quot; en cada venta marcada para regenerarlas.
                    </p>
                  </div>
                </div>
              </Card>
            )}

            {/* Sales List with Products */}
            {sales.length > 0 && (
              <Card className="bg-zinc-900/50 border-zinc-800 p-6">
                <div className="flex items-center gap-2 mb-4">
                  <ShoppingBag className="w-4 h-4 text-gold" />
                  <h3 className="text-sm text-zinc-500 uppercase tracking-widest">
                    Ventas Registradas ({sales.filter(s => !s.cancelled).length})
                    {sales.filter(s => s.cancelled).length > 0 && (
                      <span className="text-red-400 ml-2">
                        ({sales.filter(s => s.cancelled).length} cancelada{sales.filter(s => s.cancelled).length > 1 ? 's' : ''})
                      </span>
                    )}
                  </h3>
                </div>
                <div className="space-y-4">
                  {sales.map((sale) => (
                    <div 
                      key={sale.id} 
                      className={`rounded-lg p-4 transition-all ${
                        sale.cancelled 
                          ? 'bg-zinc-900/30 border border-zinc-800/50 opacity-60' 
                          : 'bg-zinc-800/50 border border-zinc-700/50'
                      }`}
                      data-testid={`sale-${sale.id}`}
                    >
                      {/* Sale Header */}
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className="flex items-center gap-2">
                            <Clock className={`w-4 h-4 ${sale.cancelled ? 'text-zinc-600' : 'text-zinc-500'}`} />
                            <span className={`font-medium ${sale.cancelled ? 'text-zinc-500 line-through' : 'text-white'}`}>
                              {new Date(sale.created_at).toLocaleTimeString('es-MX', { 
                                hour: '2-digit', 
                                minute: '2-digit' 
                              })}
                            </span>
                          </div>
                          {sale.branch_name && (
                            <span className={`text-xs px-2 py-1 rounded ${
                              sale.cancelled 
                                ? 'text-zinc-600 bg-zinc-800/50' 
                                : 'text-zinc-500 bg-zinc-700/50'
                            }`}>
                              {sale.branch_name}
                            </span>
                          )}
                          {sale.user_name && (
                            <div className={`flex items-center gap-1 text-sm ${
                              sale.cancelled ? 'text-zinc-600' : 'text-zinc-500'
                            }`}>
                              <User className="w-3 h-3" />
                              {sale.user_name}
                            </div>
                          )}
                          {sale.cancelled && (
                            <span className="flex items-center gap-1 text-xs text-red-400 bg-red-500/10 px-2 py-1 rounded">
                              <Ban className="w-3 h-3" />
                              Cancelada
                              {sale.cancelled_by_name && ` por ${sale.cancelled_by_name}`}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="flex items-center gap-1">
                            {getPaymentMethodIcon(sale.payment_method)}
                            <span className={`text-xs ${sale.cancelled ? 'text-zinc-600' : 'text-zinc-400'}`}>
                              {getPaymentMethodLabel(sale.payment_method)}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            {/* Show original total if items were cancelled */}
                            {sale.items?.some(i => i.cancelled) && !sale.cancelled && sale.original_total && sale.original_total !== sale.total && (
                              <span className="text-sm text-zinc-500 line-through">
                                ${sale.original_total.toFixed(2)}
                              </span>
                            )}
                            <span className={`font-heading text-xl ${
                              sale.cancelled ? 'text-zinc-500 line-through' : 'text-gold'
                            }`}>
                              ${sale.total.toFixed(2)}
                            </span>
                          </div>
                          
                          {/* Cancel/Restore Button - Only for Master Admin */}
                          {isMasterAdmin && (
                            sale.cancelled ? (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleRestoreSale(sale)}
                                disabled={actionLoading}
                                className="h-8 text-green-400 hover:text-green-300 hover:bg-green-500/10"
                                data-testid={`restore-sale-${sale.id}`}
                              >
                                <RotateCcw className="w-4 h-4 mr-1" />
                                Restaurar
                              </Button>
                            ) : (
                              <>
                                {salesWithIssues().has(sale.id) && (
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => recomputeSaleCommissions(sale)}
                                    disabled={recomputingSale === sale.id}
                                    className="h-8 text-amber-400 hover:text-amber-300 hover:bg-amber-500/10"
                                    data-testid={`recompute-sale-${sale.id}`}
                                    title="Regenerar comisiones faltantes"
                                  >
                                    <Wrench className="w-4 h-4 mr-1" />
                                    {recomputingSale === sale.id ? 'Recomputando...' : 'Recomputar'}
                                  </Button>
                                )}
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => openCancelDialog(sale)}
                                  disabled={actionLoading}
                                  className="h-8 text-red-400 hover:text-red-300 hover:bg-red-500/10"
                                  data-testid={`cancel-sale-${sale.id}`}
                                >
                                  <Trash2 className="w-4 h-4 mr-1" />
                                  Cancelar
                                </Button>
                              </>
                            )
                          )}
                        </div>
                      </div>
                      
                      {/* Sale Items */}
                      <div className="border-t border-zinc-700/50 pt-3">
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                          {sale.items.map((item, idx) => (
                            <div 
                              key={idx}
                              className={`flex items-center gap-2 px-3 py-2 rounded relative group ${
                                sale.cancelled || item.cancelled
                                  ? 'bg-zinc-800/30 border border-zinc-700/30' 
                                  : item.item_type === 'service' 
                                    ? 'bg-gold/10 border border-gold/20' 
                                    : 'bg-blue-500/10 border border-blue-500/20'
                              }`}
                              data-testid={`sale-item-${sale.id}-${idx}`}
                            >
                              {item.item_type === 'service' ? (
                                <Scissors className={`w-4 h-4 flex-shrink-0 ${
                                  sale.cancelled || item.cancelled ? 'text-zinc-600' : 'text-gold'
                                }`} />
                              ) : (
                                <Package className={`w-4 h-4 flex-shrink-0 ${
                                  sale.cancelled || item.cancelled ? 'text-zinc-600' : 'text-blue-400'
                                }`} />
                              )}
                              <div className="flex-1 min-w-0">
                                <p className={`text-sm font-medium truncate ${
                                  sale.cancelled || item.cancelled
                                    ? 'text-zinc-500 line-through' 
                                    : item.item_type === 'service' ? 'text-gold' : 'text-blue-400'
                                }`}>
                                  {item.name}
                                  {item.cancelled && !sale.cancelled && (
                                    <span className="ml-1 text-xs text-red-400 no-underline">(cancelado)</span>
                                  )}
                                </p>
                                <p className={`text-xs ${sale.cancelled || item.cancelled ? 'text-zinc-600' : 'text-zinc-500'}`}>
                                  ${item.price.toFixed(2)} × {item.quantity}
                                </p>
                                {barberNameOf(item) && (
                                  <span
                                    className={`inline-flex items-center gap-1 mt-1 px-1.5 py-0.5 rounded-sm text-[10px] font-medium ${
                                      sale.cancelled || item.cancelled
                                        ? 'bg-zinc-800/50 text-zinc-500'
                                        : 'bg-zinc-800/70 text-zinc-300 border border-zinc-700/50'
                                    }`}
                                    data-testid={`item-barber-${sale.id}-${idx}`}
                                  >
                                    <User className="w-2.5 h-2.5" />
                                    {barberNameOf(item)}
                                  </span>
                                )}
                              </div>
                              <span className={`text-sm font-semibold ${
                                sale.cancelled || item.cancelled
                                  ? 'text-zinc-500 line-through' 
                                  : item.item_type === 'service' ? 'text-gold' : 'text-blue-400'
                              }`}>
                                ${(item.price * item.quantity).toFixed(2)}
                              </span>
                              
                              {/* Item Cancel/Restore Button - Only for Master Admin and not fully cancelled sale */}
                              {isMasterAdmin && !sale.cancelled && (
                                item.cancelled ? (
                                  <button
                                    onClick={() => handleRestoreItem(sale, idx)}
                                    disabled={actionLoading}
                                    className="ml-1 p-1 rounded text-green-400 hover:text-green-300 hover:bg-green-500/10 transition-colors"
                                    title="Restaurar item"
                                    data-testid={`restore-item-${sale.id}-${idx}`}
                                  >
                                    <RotateCcw className="w-3.5 h-3.5" />
                                  </button>
                                ) : (
                                  <button
                                    onClick={() => handleCancelItem(sale, idx)}
                                    disabled={actionLoading}
                                    className="ml-1 p-1 rounded text-red-400 hover:text-red-300 hover:bg-red-500/10 opacity-0 group-hover:opacity-100 transition-all"
                                    title="Cancelar item"
                                    data-testid={`cancel-item-${sale.id}-${idx}`}
                                  >
                                    <Ban className="w-3.5 h-3.5" />
                                  </button>
                                )
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            )}
          </>
        )}

        {/* Cancel Sale Confirmation Dialog */}
        <AlertDialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
          <AlertDialogContent className="bg-zinc-900 border-zinc-800">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-white flex items-center gap-2">
                <Trash2 className="w-5 h-5 text-red-500" />
                Cancelar Venta
              </AlertDialogTitle>
              <AlertDialogDescription className="text-zinc-400">
                ¿Estás seguro de que deseas cancelar esta venta de <span className="text-gold font-semibold">${selectedSale?.total?.toFixed(2)}</span>?
                <br /><br />
                La venta permanecerá en el registro como evidencia (tachada y en gris) pero:
                <ul className="list-disc list-inside mt-2 text-zinc-500">
                  <li>No sumará a las ventas totales</li>
                  <li>No afectará el balance ni ingresos</li>
                  <li>Las comisiones asociadas serán canceladas</li>
                  <li>Las propinas asociadas serán canceladas</li>
                </ul>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="bg-zinc-800 border-zinc-700 text-white hover:bg-zinc-700">
                No, mantener venta
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={handleCancelSale}
                disabled={actionLoading}
                className="bg-red-500 text-white hover:bg-red-600"
              >
                {actionLoading ? 'Cancelando...' : 'Sí, cancelar venta'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Assign Barber Dialog for Recompute */}
        <AlertDialog open={assignDialog.open} onOpenChange={(o) => !o && setAssignDialog({ open: false, sale: null, items: [], barberById: {} })}>
          <AlertDialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-lg" data-testid="assign-barber-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-white flex items-center gap-2">
                <Wrench className="w-5 h-5 text-amber-400" />
                Asignar barbero para regenerar comisiones
              </AlertDialogTitle>
              <AlertDialogDescription className="text-zinc-400">
                Estos items no tienen comisión registrada. Selecciona el barbero real para cada uno y presiona &quot;Regenerar&quot;.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <div className="space-y-3 py-2 max-h-[400px] overflow-y-auto">
              {assignDialog.items.map((it) => (
                <div key={it.item_index} className="bg-zinc-800/50 border border-zinc-700 rounded-sm p-3">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      {it.item_type === 'service' ? <Scissors className="w-4 h-4 text-gold" /> : <Package className="w-4 h-4 text-blue-400" />}
                      <span className="text-sm text-white">{it.item_name}</span>
                      <span className="text-xs text-zinc-500">· ${it.price?.toFixed(2)}</span>
                    </div>
                    <span className="text-xs text-red-400">
                      {it.reason_if_missing === 'no_barber_id' && 'Sin barbero'}
                      {it.reason_if_missing === 'receptionist_on_service' && `Recep: ${it.recorded_barber_name || '?'}`}
                      {it.reason_if_missing === 'unknown_barber_id' && 'ID desconocido'}
                    </span>
                  </div>
                  <Select
                    value={assignDialog.barberById[it.item_index] || ''}
                    onValueChange={(v) => setAssignDialog((d) => ({ ...d, barberById: { ...d.barberById, [it.item_index]: v } }))}
                  >
                    <SelectTrigger className="bg-zinc-900 border-zinc-700 text-white h-9" data-testid={`assign-barber-select-${it.item_index}`}>
                      <SelectValue placeholder="Selecciona barbero..." />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800 max-h-64">
                      {allBarbers.map((b) => (
                        <SelectItem key={b.id} value={b.id} className="text-white">
                          {b.name} <span className="text-zinc-500 text-xs ml-1">({b.branch_name || '—'})</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>
            <AlertDialogFooter>
              <AlertDialogCancel className="bg-zinc-800 border-zinc-700 text-zinc-300 hover:bg-zinc-700">
                Cancelar
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={confirmAssignment}
                disabled={recomputingSale != null}
                className="bg-amber-500 text-black hover:bg-amber-400"
                data-testid="confirm-assign-btn"
              >
                {recomputingSale ? 'Regenerando...' : 'Regenerar comisiones'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </AdminLayout>
  );
}
